import React, { useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSession } from '../context/SessionContext';
import { useBranding } from '../context/BrandingContext';
import AddressInput from '../components/AddressInput/AddressInput';
import DeliveryQuote from '../components/DeliveryQuote/DeliveryQuote';
import { useDeliveryQuote } from '../hooks/useDeliveryQuote';
import './DeliveryAddressPage.css';

// Haversine distance in km
function haversineDistance(lat1, lng1, lat2, lng2) {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function DeliveryAddressPage() {
  const { t } = useTranslation();
  const { restaurantId } = useParams();
  const navigate = useNavigate();
  const { updateSession } = useSession();
  const { branding, orderModes, restaurantLocation } = useBranding();

  const [address, setAddress] = useState('');
  const [addressCoords, setAddressCoords] = useState(null);
  const [apartment, setApartment] = useState('');
  const [instructions, setInstructions] = useState('');
  const [radiusError, setRadiusError] = useState('');
  const [gettingLocation, setGettingLocation] = useState(false);
  const [locationError, setLocationError] = useState('');

  const deliveryConfig = orderModes?.delivery || {};
  const maxRadius = deliveryConfig.radiusKm || 5;

  // Delivery quote from backend
  const {
    quote,
    outOfRange: quoteOutOfRange,
    loading: quoteLoading,
  } = useDeliveryQuote(restaurantId, addressCoords);

  const validateRadius = useCallback(
    (coords) => {
      setRadiusError('');
      if (!restaurantLocation?.lat || !restaurantLocation?.lng || !coords) return true;

      const dist = haversineDistance(
        parseFloat(restaurantLocation.lat),
        parseFloat(restaurantLocation.lng),
        coords.lat,
        coords.lng
      );

      if (dist > maxRadius) {
        setRadiusError(
          t('deliveryAddress.outsideRadius', { radius: maxRadius })
        );
        return false;
      }
      return true;
    },
    [restaurantLocation, maxRadius, t]
  );

  const handleAddressChange = useCallback(
    (newAddress, newCoords) => {
      setAddress(newAddress);
      setAddressCoords(newCoords);
      if (newCoords) {
        validateRadius(newCoords);
      }
    },
    [validateRadius]
  );

  const handleUseCurrentLocation = () => {
    if (!navigator.geolocation) {
      setLocationError(t('deliveryAddress.geoNotSupported'));
      return;
    }

    setGettingLocation(true);
    setLocationError('');

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const coords = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };

        // Reverse geocode if Google Maps is available
        if (window.google?.maps?.Geocoder) {
          const geocoder = new window.google.maps.Geocoder();
          geocoder.geocode({ location: coords }, (results, status) => {
            if (status === 'OK' && results[0]) {
              handleAddressChange(results[0].formatted_address, coords);
            } else {
              handleAddressChange(`${coords.lat.toFixed(6)}, ${coords.lng.toFixed(6)}`, coords);
            }
            setGettingLocation(false);
          });
        } else {
          handleAddressChange(`${coords.lat.toFixed(6)}, ${coords.lng.toFixed(6)}`, coords);
          setGettingLocation(false);
        }
      },
      () => {
        setLocationError(t('deliveryAddress.geoError'));
        setGettingLocation(false);
      }
    );
  };

  const handleConfirm = () => {
    if (!address.trim()) return;

    if (addressCoords && !validateRadius(addressCoords)) {
      return;
    }

    const deliveryAddress = {
      street: address,
      apartment: apartment.trim() || undefined,
      instructions: instructions.trim() || undefined,
      coords: addressCoords,
      deliveryFee: quote?.fee || deliveryConfig.baseFee || 0,
    };

    sessionStorage.setItem('resto_delivery_address', JSON.stringify(deliveryAddress));
    updateSession({ deliveryAddress });
    navigate(`/r/${restaurantId}/delivery`);
  };

  const handleBack = () => {
    navigate(`/r/${restaurantId}`);
  };

  const isOutOfRange = !!radiusError || quoteOutOfRange;
  const canConfirm = address.trim() && !isOutOfRange && !quoteLoading;

  return (
    <div className="delivery-address-page">
      <div className="delivery-address-container">
        <div className="delivery-address-header">
          <button type="button" className="back-button" onClick={handleBack}>
            <span className="material-icons" aria-hidden="true">arrow_back</span>
          </button>
          <h1>{t('deliveryAddress.title')}</h1>
        </div>

        {branding.restaurantName && (
          <p className="delivery-restaurant-name">{branding.restaurantName}</p>
        )}

        <div className="delivery-address-form">
          {/* Address input with Google Places autocomplete + map */}
          <AddressInput
            value={address}
            coords={addressCoords}
            onChange={handleAddressChange}
          />

          {/* Use current location button */}
          <button
            type="button"
            className="gps-button"
            onClick={handleUseCurrentLocation}
            disabled={gettingLocation}
          >
            <span className="material-icons" aria-hidden="true">my_location</span>
            {gettingLocation
              ? t('deliveryAddress.gettingLocation')
              : t('deliveryAddress.useMyLocation')}
          </button>

          {locationError && <p className="form-error">{locationError}</p>}
          {radiusError && <p className="form-error">{radiusError}</p>}

          {/* Delivery quote from backend */}
          <DeliveryQuote
            quote={quote}
            outOfRange={quoteOutOfRange}
            loading={quoteLoading}
            radiusKm={maxRadius}
          />

          {/* Apartment / Floor */}
          <div className="form-group">
            <label htmlFor="delivery-apartment">{t('deliveryAddress.apartmentLabel')}</label>
            <input
              type="text"
              id="delivery-apartment"
              value={apartment}
              onChange={(e) => setApartment(e.target.value)}
              placeholder={t('deliveryAddress.apartmentPlaceholder')}
            />
          </div>

          {/* Delivery instructions */}
          <div className="form-group">
            <label htmlFor="delivery-instructions">{t('deliveryAddress.instructionsLabel')}</label>
            <textarea
              id="delivery-instructions"
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              placeholder={t('deliveryAddress.instructionsPlaceholder')}
              rows={3}
            />
          </div>

          {/* Static delivery info (baseFee / minOrder from config — shown when no backend quote yet) */}
          {!quote && !quoteLoading && deliveryConfig.baseFee > 0 && (
            <div className="delivery-info">
              <span className="material-icons" aria-hidden="true">local_shipping</span>
              <span>{t('deliveryAddress.deliveryFee', { fee: deliveryConfig.baseFee })}</span>
            </div>
          )}

          {deliveryConfig.minOrder > 0 && (
            <div className="delivery-info">
              <span className="material-icons" aria-hidden="true">shopping_cart</span>
              <span>{t('deliveryAddress.minOrder', { amount: deliveryConfig.minOrder })}</span>
            </div>
          )}

          {/* Confirm button */}
          <button
            type="button"
            className="confirm-button"
            onClick={handleConfirm}
            disabled={!canConfirm}
          >
            {t('deliveryAddress.confirm')}
            <span className="material-icons" aria-hidden="true">arrow_forward</span>
          </button>
        </div>
      </div>
    </div>
  );
}

export default DeliveryAddressPage;
